import { supabase } from '../lib/supabase';

/**
 * Generate Pinterest-optimized title and description using OpenAI via Supabase Edge Function
 * @param {string} articleTitle - Original article title
 * @param {string} metaDescription - Article meta description
 * @param {string} focusKeyword - Article focus keyword
 * @param {string} language - Language code (en/de/pl/fr/es) the pin content should be written in
 * @returns {Promise<{pinTitle: string, pinDescription: string}>}
 */
export async function generatePinterestContent(articleTitle, metaDescription, focusKeyword, language = 'en', options = {}) {
  try {
    // Call Supabase Edge Function instead of OpenAI directly
    const { data, error } = await supabase?.functions?.invoke('generate-pinterest-content', {
      body: {
        articleTitle,
        metaDescription,
        focusKeyword,
        language,
        ...options
      }
    });

    if (error) {
      console.error('Error calling generate-pinterest-content function:', error);
      if (options.freshVariation && error.context?.json) {
        const details = await error.context.json().catch(() => null);
        if (details?.error) throw new Error(details.error);
      }

      // Check for specific error types
      if (error?.message?.includes('OpenAI API key not configured')) {
        throw new Error('OpenAI API key is not configured in Supabase Edge Functions. Please configure OPENAI_API_KEY in your Supabase project settings under Edge Functions secrets.');
      }

      if (error?.message?.includes('FunctionsHttpError') || error?.message?.includes('non-2xx status')) {
        throw new Error('Pinterest content generation service is unavailable. Please check your Supabase Edge Function configuration.');
      }

      throw new Error(`Failed to generate Pinterest content: ${error.message}`);
    }

    if (!data?.pinTitle || !data?.pinDescription) {
      throw new Error('Invalid response from Pinterest content generation');
    }

    return {
      pinTitle: data?.pinTitle,
      pinDescription: data?.pinDescription
    };
  } catch (error) {
    console.error('Error generating Pinterest content:', error);
    // Re-throw with the original error message to preserve context
    throw error;
  }
}

// REMOVED: generatePinImage - Now using client-side JSX generation via pinImageAutomationService.js

/**
 * Save Pinterest pin to library
 * @param {Object} pinData - Pin data to save
 * @returns {Promise<Object>} - Saved pin record
 */
export async function savePinToLibrary(pinData) {
  try {
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase?.from('pinterest_pins')?.insert([
        {
          user_id: user?.id,
          profile_id: pinData?.profileId || null,
          pin_title: pinData?.pinTitle,
          pin_description: pinData?.pinDescription,
          article_url: pinData?.articleUrl,
          article_title: pinData?.articleTitle,
          article_meta_description: pinData?.articleMetaDescription,
          image_url: pinData?.imageUrl,
          template_name: pinData?.templateName,
          website_name: pinData?.websiteName,
          status: 'ready'
        }
      ])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving pin to library:', error);
    throw new Error(`Failed to save pin: ${error.message}`);
  }
}

/**
 * Fetch a single page of Pinterest pins from the library (server-side pagination).
 * Returns { pins, totalCount } where totalCount is the full count matching the filters.
 * Maximum supported capacity: 600 pins (enforced by the library UI limit).
 *
 * @param {Object} filters - Optional filters (status, search, startDate, endDate, pinterestAccountId)
 * @param {number} page - 1-based page number
 * @param {number} pageSize - Number of pins per page (default 20)
 * @returns {Promise<{ pins: Array, totalCount: number }>}
 */
export async function fetchPinsFromLibrary(filters = {}, page = 1, pageSize = 20) {
  try {
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase?.from('pinterest_pins')
      ?.select('*', { count: 'exact' })
      ?.eq('user_id', user?.id)
      ?.order('created_at', { ascending: false })
      ?.range(from, to);

    if (filters?.profileId) {
      query = query?.eq('profile_id', filters?.profileId);
    }

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    if (filters?.search) {
      query = query?.or(`pin_title.ilike.%${filters?.search}%,article_title.ilike.%${filters?.search}%`);
    }

    if (filters?.startDate) {
      query = query?.gte('created_at', filters?.startDate);
    }

    if (filters?.endDate) {
      query = query?.lte('created_at', filters?.endDate);
    }

    if (filters?.pinterestAccountId) {
      query = query?.eq('pinterest_account_id', filters?.pinterestAccountId);
    }

    const { data, error, count } = await query;

    if (error) throw error;
    return { pins: data || [], totalCount: count || 0 };
  } catch (error) {
    console.error('Error fetching pins from library:', error);
    throw new Error(`Failed to fetch pins: ${error.message}`);
  }
}

/**
 * Fetch pin counts grouped by status for the given non-status filters.
 * Runs 5 lightweight HEAD (count-only) queries in parallel.
 *
 * @param {Object} baseFilters - Filters WITHOUT status (search, startDate, endDate, pinterestAccountId)
 * @returns {Promise<{ all: number, ready: number, published: number, scheduled: number, failed: number }>}
 */
export async function fetchPinStatusCounts(baseFilters = {}) {
  try {
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const buildCountQuery = (status) => {
      let q = supabase?.from('pinterest_pins')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('user_id', user?.id);

      if (baseFilters?.profileId) q = q?.eq('profile_id', baseFilters?.profileId);
      if (status) q = q?.eq('status', status);
      if (baseFilters?.search) q = q?.or(`pin_title.ilike.%${baseFilters?.search}%,article_title.ilike.%${baseFilters?.search}%`);
      if (baseFilters?.startDate) q = q?.gte('created_at', baseFilters?.startDate);
      if (baseFilters?.endDate) q = q?.lte('created_at', baseFilters?.endDate);
      if (baseFilters?.pinterestAccountId) q = q?.eq('pinterest_account_id', baseFilters?.pinterestAccountId);

      return q;
    };

    const [allRes, readyRes, publishedRes, scheduledRes, failedRes] = await Promise.all([
      buildCountQuery(null),
      buildCountQuery('ready'),
      buildCountQuery('published'),
      buildCountQuery('scheduled'),
      buildCountQuery('failed'),
    ]);

    return {
      all: allRes.count || 0,
      ready: readyRes.count || 0,
      published: publishedRes.count || 0,
      scheduled: scheduledRes.count || 0,
      failed: failedRes.count || 0,
    };
  } catch (error) {
    console.error('Error fetching pin status counts:', error);
    return { all: 0, ready: 0, published: 0, scheduled: 0, failed: 0 };
  }
}

/**
 * Delete pin from library
 * @param {string} pinId - Pin ID to delete
 * @returns {Promise<void>}
 */
export async function deletePinFromLibrary(pinId) {
  try {
    const { error } = await supabase?.from('pinterest_pins')?.delete()?.eq('id', pinId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting pin:', error);
    throw new Error(`Failed to delete pin: ${error.message}`);
  }
}

/**
 * Delete multiple pins from library
 * @param {string[]} pinIds - Pin IDs to delete
 * @returns {Promise<void>}
 */
export async function deletePinsFromLibrary(pinIds = []) {
  if (!pinIds?.length) return;
  try {
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('pinterest_pins')
      .delete()
      .eq('user_id', user?.id)
      .in('id', pinIds);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error deleting pins:', error);
    throw new Error(`Failed to delete pins: ${error.message}`);
  }
}

export async function updatePinsStatus(pinIds = [], status = 'published') {
  if (!pinIds?.length) return;
  try {
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('pinterest_pins')
      .update({ status })
      .eq('user_id', user?.id)
      .in('id', pinIds);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error updating pin status:', error);
    throw new Error(`Failed to update pin status: ${error.message}`);
  }
}
